/**
 * Аналіз субконто (ч.1036 — переписано): значення субконто (контрагент,
 * працівник, номенклатура…) → усі рахунки, де воно є, з сальдо й оборотами.
 * Прецедент Адонари: 631 + 6851 + 3711 на одному екрані. Клік по рахунку —
 * картка з відбором по цьому субконто.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type SubcontoAnalysisRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { ContractorFilter } from '@/shared/components/DocListFilters';
import LedgerFilters, { NoRegulatedAccess } from './ledger/LedgerFilters';
import { useLedgerParams } from './ledger/useLedgerParams';
import { balanceColumns, periodLabel, TreeLabel, LABEL_W } from './ledger/ledgerColumns';
import { ledgerLinks, toggleCollapsed, visibleRows } from './ledger/ledgerModel';

type Row = SubcontoAnalysisRow & { _idx: string; _parent: string; _kind?: 'total' };

export default function SubcontoAnalysisPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const typesQ = useQuery({
    queryKey: ['ledger-sc-types', st.org],
    queryFn: () => reports.subcontoTypes(st.org),
    enabled: !!st.org,
  });
  const type = st.get('type') || (typesQ.data?.[0]?.type ?? '');
  const id = st.get('id');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ['ledger-sa', st.org, type, id, st.from, st.to],
    queryFn: () => reports.subcontoAnalysis({
      organization_id: st.org, subconto_type: type, date_from: st.from, date_to: st.to,
      subconto_id: id || undefined }),
    enabled: !!st.org && !!type,
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    let parent = '';
    let keep = true;
    const all: Row[] = [];
    data.rows.forEach((r, i) => {
      if (r.kind === 'subconto') {
        parent = r.label;
        keep = !needle || r.label.toLowerCase().includes(needle);
      }
      if (keep) all.push({ ...r, _idx: String(i), _parent: parent });
    });
    const out = visibleRows(all, collapsed, (r) => r._idx);
    if (!needle) {
      out.push({ ...data.totals, kind: 'total', level: 0, label: 'Разом', subconto_type: type,
        subconto_id: null, account_code: null, account_name: null, is_group: false,
        _idx: '_total', _parent: '', _kind: 'total' });
    }
    return out;
  }, [data, collapsed, search, type]);

  const cardLink = (r: Row) => r.account_code
    ? ledgerLinks.accountCard({ ...period, account: r.account_code, sc: r.subconto_id, scName: r._parent })
    : null;

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'label', header: 'Субконто / рахунок', width: LABEL_W,
      cell: (r) => r._kind ? <strong>{r.label}</strong> : (
        <TreeLabel level={r.level} isGroup={r.is_group} collapsed={collapsed.has(r._idx)}
          onToggle={() => setCollapsed((s) => toggleCollapsed(s, r._idx))}>
          {r.kind === 'account'
            ? <a className="ledger__link" title="Картка рахунку з відбором по цьому субконто"
                onClick={(e) => { e.stopPropagation(); const l = cardLink(r); if (l) navigate(l); }}>{r.label}</a>
            : <a className="ledger__link" title="Картка субконто: усі проводки по всіх рахунках"
                onClick={(e) => { e.stopPropagation(); navigate(ledgerLinks.accountCard({ ...period, account: null, sc: r.subconto_id, scName: r.label })); }}>{r.label}</a>}
        </TreeLabel>
      ),
    },
    ...balanceColumns<Row>(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [collapsed, st.org, st.from, st.to]);

  if (!st.permsLoading && !st.hasPermission) {
    return <NoRegulatedAccess title="Аналіз субконто" total={st.totalAccessibleOrgs} />;
  }
  const typeLabel = typesQ.data?.find((t) => t.type === type)?.label ?? data?.subconto_label ?? '';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Аналіз субконто{typeLabel ? `: ${typeLabel.toLowerCase()}` : ''}
          {' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span>
        </h2>
      </div>

      <LedgerFilters state={st}
        printHref={type ? ledgerLinks.print('sa', { ...period, type, id: id || undefined, name: st.get('name') || undefined }) : undefined}
        onExcel={type ? () => reports.ledgerXlsx('/reports/subconto-analysis', {
          organization_id: st.org, subconto_type: type, date_from: st.from, date_to: st.to,
          subconto_id: id || undefined }) : undefined}
        disabled={!data}>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Вид субконто</label>
          <select value={type} onChange={(e) => st.set({ type: e.target.value, id: null, name: null })}>
            {(typesQ.data ?? []).map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
          </select>
        </div>
        {type === 'ref.contractors' ? (
          <div className="field" style={{ minWidth: 260 }}>
            <label>Контрагент</label>
            <ContractorFilter value={id} onChange={(v) => st.set({ id: v || null, name: null })} label="усі" />
          </div>
        ) : (
          <div className="field" style={{ minWidth: 220 }}>
            <label>Пошук за назвою</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="частина назви" />
          </div>
        )}
      </LedgerFilters>

      {q.isError && <p className="ledger__warn">Не вдалося завантажити звіт.</p>}

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => r._idx}
        loading={q.isLoading || typesQ.isLoading}
        emptyText={type ? 'За період рухів з таким субконто немає.' : 'У проводках організації немає субконто.'}
        rowBold={(r) => r.is_group || !!r._kind}
        rowBackground={(r) => r._kind ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => { const l = cardLink(r) ?? (r.kind === 'subconto' ? ledgerLinks.accountCard({ ...period, account: null, sc: r.subconto_id, scName: r.label }) : null); if (l) navigate(l); }}
        storageKey="ledger-subconto-analysis"
      />
      {data && (
        <div className="ledger__totals">
          <span className="ledger__hint">рядок субконто — чисте сальдо по всіх рахунках (як в акті звірки), клік — картка субконто; клік по рахунку — картка з відбором</span>
        </div>
      )}
    </div>
  );
}
